import Block, { BLOCK_MATRIX } from "./block";
import Palette from "./palette";
import { ICanvas } from "../types/canvas";
import { CanvasStatus } from "../types";
import Random from './random';
import Bitmap from '../bitmap';
import Color from './color';
import { getBlockPackForCanvas } from '../packs/blocks/BlockPackFactory';

export default class Canvas implements ICanvas {
  private static instanceCounter = 0;
  private instanceId: number;
  level: number;
  idle: number = 1;
  dying: boolean;
  speed: number;
  downSpeed: number = 0.02;
  horizontalRepeat: number = 2;
  horizontalRepeatDelay: number;
  verticalRepeat: number = 2;
  verticalRepeatDelay: number;
  sideSpeed: number;
  shadow: boolean = true;
  bloc?: Block;           // Current falling piece
  blocShadow?: Block;     // Shadow of current piece
  next?: Block;           // Next piece (preview 1)
  next2?: Block;          // Next piece (preview 2)
  next3?: Block;          // Next piece (preview 3)
  rnd: Random;            // Random number generator for deterministic pieces
  waitDownload: boolean = false;  // Wait for P_DOWNLOAD packet (remote games only)
  daMoves: number[] = []; // Move buffer for remote players (matches original canvas->da_moves)
  moveIndex: number = 0; // Index into daMoves for processing (CRITICAL: must be in Canvas, not WaitBlockState, to persist across state instances)
  blinded: number[][] = new Array(36);
  dirted: number[][] = new Array(36);
  bflash: number[][] = new Array(36);
  flashAnim: number;
  flashBlocks: number[][] = new Array(36);
  occupied: boolean[][] = new Array(36);
  blocks: number[][] = new Array(36);
  //unit block moved in last Player_check_link anim
  moved: boolean[][] = new Array(36);
  holePos: boolean[][] = new Array(36);
  tmp: number[][] = new Array(33);
  depth: number;
  complexity: number;
  sendForClean: boolean;
  smooth: boolean = true;
  collideSideOnly: boolean;
  palette: Palette;
  bestClean: number = 0;
  status: CanvasStatus;
  bonus: number = 0;
  bon: Array<{
    x: number;
    color: number;
    blind_time: number;
    hole_pos: number;
    final: boolean;
  }> = new Array(20);
  lastAttackerNumPlayer?: number; // Player number (0-5) of last attacker (undefined = no attacker, matching original 255)
  attackCounts: Record<number, number> = {}; // Maps numPlayer (0-5) to attack count (like original attacks[MAXPLAYERS])

  // Move packet state (matches original Canvas::moves and related)
  private movesPacket: { player: number; size: number; moves: number[] } | null = null;
  private currentMoveByte: number = 0;
  private currentMoveBitCount: number = 0;
  game?: any; // Reference to game instance (for getWantsMoves and getMultiplayerAdapter)
  numPlayer: number = 0; // Player number for this canvas

  constructor(gameSeed?: number) {
    this.instanceId = Canvas.instanceCounter++;
    console.log(`[Canvas] Created new Canvas instance #${this.instanceId}${gameSeed !== undefined ? ` with seed=${gameSeed}` : ''}`);

    // Initialize random number generator
    // If gameSeed provided, use it; otherwise use Date.now() (matches original Canvas constructor)
    this.rnd = gameSeed !== undefined ? new Random(gameSeed) : new Random();
    
    if (gameSeed !== undefined) {
      console.log(`[Canvas] RNG initialized with seed=${gameSeed}, first rnd() will return ${this.rnd.get_seed()}`);
    }

    switch (this.horizontalRepeat) {
      case 0:
        this.horizontalRepeatDelay = 11;
        break;
      case 1:
        this.horizontalRepeatDelay = 6;
        break;
      case 2:
        this.horizontalRepeatDelay = 3;
        break;
      case 3:
        this.horizontalRepeatDelay = 1;
        break;
    }
    this.sideSpeed = (18 << 4) / this.horizontalRepeatDelay;
    switch (this.verticalRepeat) {
      case 0:
        this.verticalRepeatDelay = 11;
        break;
      case 1:
        this.verticalRepeatDelay = 6;
        break;
      case 2:
        this.verticalRepeatDelay = 3;
        break;
      case 3:
        this.verticalRepeatDelay = 1;
        break;
    }

    this.sideSpeed = (18 << 4) / this.horizontalRepeatDelay;
    this.downSpeed = 340 / this.verticalRepeatDelay;

    for (let i = 0; i < this.occupied.length; i++) {
      this.occupied[i] = new Array(18);
    }
    for (let i = 0; i < this.blinded.length; i++) {
      this.blinded[i] = new Array(18);
    }
    for (let i = 0; i < this.dirted.length; i++) {
      this.dirted[i] = new Array(18);
    }
    for (let i = 0; i < this.bflash.length; i++) {
      this.bflash[i] = new Array(18);
    }
    for (let i = 0; i < this.blocks.length; i++) {
      this.blocks[i] = new Array(18);
    }
    for (let i = 0; i < this.tmp.length; i++) {
      this.tmp[i] = new Array(18);
    }
    for (let i = 0; i < this.moved.length; i++) {
      this.moved[i] = new Array(18);
    }
    for (let i = 0; i < this.moved.length; i++) {
      this.holePos[i] = new Array(18);
    }
    for (let i = 0; i < this.flashBlocks.length; i++) {
      this.flashBlocks[i] = new Array(18);
    }
    this.init();
  }

  init(): void {
    this.level = 1;
    this.calcSpeed(); // Initialize speed based on starting level
    this.reinit();
  }

  reinit(): void {
    // Matches original canvas.cc:180-231 (reinit)
    this.depth = this.complexity;
    this.sendForClean = false;
    
    // Initialize occupied and block arrays (matches original lines 184-190)
    for (let j = 0; j < 36; j++) {
      for (let i = 0; i < 18; i++) {
        this.occupied[j][i] = false;
        this.blocks[j][i] = 0;
        this.blinded[j][i] = 0;
        this.bflash[j][i] = 0;
      }
    }
    
    // Initialize flash array
    for (let i = 0; i < 20; i++) {
      this.flashAnim = 0;
    }
    
    // Call init_block to set up board boundaries (walls/floor)
    this.initBlock();
  }
  
  /**
   * Initialize block boundaries (walls and floor)
   * Matches original canvas.cc:307-338 (init_block)
   */
  initBlock(): void {
    // Set floor (bottom 4 rows)
    for (let y = 32; y < 36; y++) {
      for (let x = 0; x < 18; x++) {
        this.occupied[y][x] = true;
      }
    }
    
    // Set walls and clear playfield
    for (let y = 0; y < 32; y++) {
      // Left wall (4 columns)
      for (let x = 0; x < 4; x++) {
        this.occupied[y][x] = true;
      }
      // Right wall (4 columns)
      for (let x = 14; x < 18; x++) {
        this.occupied[y][x] = true;
      }
      // Playfield (10 columns in middle)
      for (let x = 4; x < 14; x++) {
        this.blocks[y][x] = 0;
        this.occupied[y][x] = false;
      }
    }
    
    // Initialize tmp array for boundaries
    for (let x = 0; x < 18; x++) {
      this.tmp[32][x] = 1;
    }
    for (let y = 0; y < 32; y++) {
      for (let x = 0; x < 4; x++) {
        this.tmp[y][x] = 1;
      }
      for (let x = 14; x < 18; x++) {
        this.tmp[y][x] = 1;
      }
    }
    
    // Mark all cells as dirty for redraw
    for (let y = 0; y < 36; y++) {
      for (let x = 0; x < 14; x++) {
        this.dirted[y][x] = 2;
      }
    }
    
    // Clear blinded and bflash
    for (let y = 0; y < 36; y++) {
      for (let x = 0; x < 18; x++) {
        this.blinded[y][x] = 0;
        this.bflash[y][x] = 0;
      }
    }
  }

  restart() {
    console.log(`[Canvas.restart] Restarting canvas (numPlayer=${this.numPlayer}), RNG seed=${this.rnd.get_seed()}, clearing ${this.daMoves.length} queued moves`);
    
    // Match original Canvas::restart() (canvas.cc:263-288)
    // Original: clear_key_all() (for local only)
    // Original: init_block()
    this.initBlock();
    
    // Original: clear_tmp()
    this.clearTmp();
    
    // Clear move buffer (matches behavior of Player_wait_block destructor clearing da_moves)
    // When a player dies and respawns, the old WaitBlockState is destroyed (clearing da_moves)
    // and a new one is created. Canvas.restart() should do the same.
    this.daMoves = [];
    this.moveIndex = 0; // Reset move index when restarting
    
    // Original: delete_bloc() - clear all pieces (bloc, next, next2, next3, bloc_shadow)
    // Reference: quadra/source/canvas.cc:150-162
    this.bloc = null;
    this.next = null;
    this.next2 = null;
    this.next3 = null;
    this.blocShadow = null;
    
    // Original: set_next() - only updates UI zones, does NOT generate pieces
    // Reference: quadra/source/canvas.cc:359-365
    // Pieces are generated later:
    // - For remote games with waitDownload=true: Set by P_DOWNLOAD packet
    // - For local games or waitDownload=false: Generated in GetNextState via shiftNext()
    this.setNext();
    
    // Original: last_attacker = 255
    this.lastAttackerNumPlayer = undefined;
    
    // Original: for(int i=0; i<MAXPLAYERS; i++) { attacks[i] = 0; handicaps[i] = 0; }
    this.attackCounts = {};
    
    // Original: level = game->level_start (we use 1 for now)
    this.level = 1;
    
    // Original: depth = complexity = bonus = 0
    this.depth = 0;
    this.complexity = 0;
    this.bonus = 0;
    
    // Clear bonus queue
    for (let i = 0; i < this.bon.length; i++) {
      this.bon[i] = {
        x: 0,
        color: 0,
        blind_time: 0,
        hole_pos: 0,
        final: false,
      };
    }
    
    // Original: calc_speed()
    this.calcSpeed();
    
    // Original: idle = 1
    this.idle = 1;
    
    // Original: state = PLAYING
    this.status = 0; // PLAYING
    
    // Original: dying=false
    this.dying = false;
    
    // Original: send_for_clean=false
    this.sendForClean = false;
    
    this.changeLevel();
  }

  /**
   * Set random seed (for remote games to sync piece generation)
   * Reference: quadra/source/player.cc:893
   */
  setSeed(seed: number): void {
    // Note: This method is rarely needed now that seed is passed to constructor
    // Only used for special cases like respawning with a different seed
    console.log(`[Canvas.setSeed] Re-setting seed=${seed} for numPlayer=${this.numPlayer}, previous seed was ${this.rnd.get_seed()}`);
    this.rnd.set_seed(seed);
  }

  /**
   * Update UI zones for next pieces (matches original Canvas::set_next())
   * Reference: quadra/source/canvas.cc:359-365
   * NOTE: Original set_next() does NOT generate pieces, it only updates UI zones
   * Pieces are generated in Player_get_next::shift_next() or set by P_DOWNLOAD
   */
  setNext(): void {
    // Original set_next() only updates UI zones (znext, znext2, znext3)
    // We don't have UI zones in our implementation, so this is a no-op
    // Pieces are generated elsewhere:
    // - For local games: Generated in GetNextState via shiftNext()
    // - For remote games with waitDownload=true: Set by P_DOWNLOAD packet
    // - For remote games with waitDownload=false: Generated in GetNextState via shiftNext()
  }

  /**
   * Shift to next piece (matches original Player_get_next::shift_next)
   * Reference: quadra/source/player.cc:263-273
   * 
   * Original behavior:
   * - If next3 is null, bloc becomes null (first iteration)
   * - If next2 is null, bloc becomes null (second iteration)
   * - If next is null, bloc becomes null (third iteration)
   * - After 3 iterations, bloc is set and we have a piece
   * 
   * This matches the original where pieces are generated on-demand,
   * not pre-generated in restart()/set_next()
   */
  shiftNext(): void {
    this.bloc = this.next3; // May be null on first few calls
    this.next3 = this.next2; // May be null on first few calls
    this.next2 = this.next; // May be null on first few calls
    // Generate new piece using deterministic random
    const theNext = this.rnd.rnd() % 7;
    this.next = new Block(theNext, -1, 7, 10);
  }

  changeLevel() {
    // let vibrant = new Vibrant(fond);
    // vibrant.getPalette().then(pal => {
    //     let palArray = Object.keys(pal).map(k => pal[k]);
    //     for(let i=0; i<6; i++){
    //         this.palette.setcolor(i+8, ...palArray[i].getRgb());
    //     }
    // });
  }

  clear() {
    let x: number, y: number;

    for (y = 32; y < 36; y++) {
      for (x = 0; x < 18; x++) {
        this.occupied[y][x] = true;
      }
    }
    for (y = 0; y < 32; y++) {
      for (x = 0; x < 4; x++) {
        this.occupied[y][x] = true;
        this.occupied[y][x + 14] = true;
      }
      for (x = 4; x < 14; x++) {
        this.blocks[y][x] = 0;
        this.occupied[y][x] = false;
      }
    }
    for (x = 0; x < 18; x++) {
      this.tmp[32][x] = 1;
    }

    for (y = 0; y < 32; y++) {
      for (x = 0; x < 4; x++) {
        this.tmp[y][x] = 1;
      }
      for (x = 14; x < 18; x++) {
        this.tmp[y][x] = 1;
      }
    }
    for (y = 0; y < 36; y++) {
      for (x = 0; x < 14; x++) {
        this.dirted[y][x] = 2;
      }
    }
    for (y = 0; y < 36; y++) {
      for (x = 0; x < 18; x++) {
        this.blinded[y][x] = 0;
        this.bflash[y][x] = 0;
      }
    }
  }

  clearTmp(): void {
    for (let j = 0; j < 32; j++) {
      for (let i = 4; i < 14; i++) {
        this.tmp[j][i] = 0;
      }
    }
    for (let j = 0; j < 36; j++) {
      for (let i = 0; i < 18; i++) {
        this.moved[j][i] = false;
      }
    }
    for (let j = 0; j < 36; j++) {
      this.flashBlocks[j] = new Array(18);
    }
    // Ensure tmp[32] is set to 1 for all columns (matching original where tmp[32] is set in clear() and preserved by clear_tmp())
    // This marks the bottom row as "connected to floor" so blocks can fall in CheckLinkState
    for (let i = 0; i < 18; i++) {
      this.tmp[32][i] = 1;
    }
  }

  calcShadow(block: Block): void {
    if (!this.blocShadow) {
      this.blocShadow = new Block(block.quel, 7, 0, 0);
    } else {
      // Always update the shadow's piece type to match the current block
      // This fixes a bug where the shadow could show the wrong piece type
      // when a new piece spawns (especially in survivor mode)
      if (this.blocShadow.quel !== block.quel) {
        this.blocShadow.quel = block.quel;
      }
    }
    this.blocShadow.rotation = block.rotation;
    this.blocShadow.x = block.x;
    this.blocShadow.y = block.y;
    while (
      !this.checkCollide(
        this.blocShadow,
        this.blocShadow.x,
        this.blocShadow.y + 1,
        this.blocShadow.rotation
      )
    ) {
      this.blocShadow.y++;
    }
    this.blocShadow.calcPosXY();
  }

  checkCollide(
    block: Block,
    px: number,
    py: number,
    rotation: number
  ): boolean {
    this.collideSideOnly = true;
    let collide: boolean = false;
    for (let j = 0; j < 4; j++) {
      for (let i = 0; i < 4; i++) {
        if (BLOCK_MATRIX[block.quel][rotation][j][i]) {
          // Safety check: ensure occupied array and row exist
          if (this.occupied && this.occupied[py + j] && this.occupied[py + j][px + i]) {
            if (px + i >= 4 && px + i < 14) this.collideSideOnly = false;
            collide = true;
          }
        }
      }
    }
    if (!collide) {
      this.collideSideOnly = false;
    }
    return collide;
  }

  calcBy(y: number) {
    return (((y + 15 + ((12 * 18) << 4)) >> 4) + 17) / 18;
  }

  calcSpeed(): void {
    if (this.level <= 10) this.speed = 4 + (this.level - 1) * 5;
    else this.speed = 50 + (this.level - 10) * 3;
  }

  addGarbageLines(
    lines: number,
    holePositions: number[],
    attackerColor: number = 7
  ): void {
    // This method is kept for backward compatibility but now just calls addPacket
    this.addPacket(attackerColor, lines, 0, "normal", holePositions);
  }

  addPacket(
    senderColor: number,
    lines: number,
    complexity: number,
    attackType: "normal" | "clean" | "potato" | "none" | "blind" | "fullblind",
    holePositions: number[],
    senderNumPlayer?: number, // Player number (0-5) of attacker (matches original qui as uint8_t)
    attackParam?: number // For blind attacks, this is the blind_time parameter
  ): void {
    // Nothing further to do if attack is none (matches original line 414-415)
    if (attackType === "none") {
      return;
    }

    // Update last_attacker and attacks array (matching original Canvas::add_packet() lines 417-427)
    if (senderNumPlayer !== undefined && senderNumPlayer >= 0 && senderNumPlayer <= 5) {
      // Update attack count: attacks[qui] += nb*2 (capped at 255)
      const currentCount = this.attackCounts[senderNumPlayer] || 0;
      const newCount = Math.min(255, currentCount + lines * 2);
      this.attackCounts[senderNumPlayer] = newCount;

      // Update last_attacker (matching original logic)
      if (this.lastAttackerNumPlayer !== undefined) {
        // If there is a last attacker, compare attack counts
        const lastAttackerCount = this.attackCounts[this.lastAttackerNumPlayer] || 0;
        if (newCount >= lastAttackerCount) {
          // If new count >= current last attacker's count, update last_attacker
          this.lastAttackerNumPlayer = senderNumPlayer;
        }
      } else {
        // If no last_attacker, set it to sender
        this.lastAttackerNumPlayer = senderNumPlayer;
      }
    }

    // If full-blind attack, blind canvas and return (matches original lines 430-432)
    if (attackType === "fullblind") {
      const blindTime = (attackParam || 12) * lines * complexity; // nb*nc*attack.param
      this.blindAll(blindTime);
      return;
    }

    // Attack type is either blind or lines, add bonuses (matches original line 435)
    if (this.bonus < 20) {
      if (lines + this.bonus > 20) {
        lines = 20 - this.bonus;
      }
      complexity--;

      // Set blind_time based on attack type (matches original line 446, 460, 470)
      // ATTACK_BLIND sets blind_time to attack.param, others set it to 0
      const blindTime = attackType === "blind" ? (attackParam || 30) : 0;

      for (let x = 0; x < lines; x++) {
        this.bon[x + this.bonus] = {
          x: 127, // Magic value indicating new complexity info is present
          color: senderColor,
          blind_time: blindTime, // Set based on attack type (ATTACK_BLIND uses param, others use 0)
          hole_pos: holePositions[x] || 0,
          final: x === lines - 1, // Last line in this packet
        };
      }
      this.bonus += lines;
    } else {
      console.warn(
        `[addPacket] Bonus queue full (${this.bonus}), ignoring ${lines} lines`
      );
    }
  }

  /**
   * Blind all occupied blocks on the canvas (matches original Canvas::blind_all)
   * Reference: quadra/source/canvas.cc:382-398
   */
  private blindAll(time: number): void {
    if (this.idle < 2 && !this.dying) {
      for (let y = 0; y < 36; y++) {
        for (let x = 0; x < 18; x++) {
          const tmp = this.blinded[y][x];
          if (this.occupied[y][x]) {
            if (!tmp && time) {
              this.bflash[y][x] = 32;
              this.dirted[y][x] = 2;
            }
            this.blinded[y][x] = Math.min(255, tmp + time);
          }
        }
      }
    }
  }

  /**
   * Render board background (occupied blocks)
   * Reference: quadra/source/canvas.cc:929-958
   */
  blitBack(bitmap: Bitmap, palette: Palette): void {
    const pack = getBlockPackForCanvas(this);
    
    for (let j = 12; j < 32; j++) {
      for (let i = 4; i < 14; i++) {
        if (this.dirted[j][i]) {
          let blitbloc = true;
          
          // Don't blit if not occupied
          if (!this.occupied[j][i]) {
            blitbloc = false;
          }
          
          // Don't blit if flashing (line clear animation)
          if (this.bflash[j][i] && (this.bflash[j][i] & 4)) {
            blitbloc = false;
          }
          
          // Don't blit if blinded
          if (!this.bflash[j][i] && this.blinded[j][i]) {
            blitbloc = false;
          }
          
          if (blitbloc) {
            this.drawBlock(bitmap, j, i, palette, pack);
          }
          
          this.dirted[j][i]--;
        }
      }
    }
  }

  /**
   * Render a single block on the board
   * Reference: quadra/source/canvas.cc:340-349
   */
  private drawBlock(bitmap: Bitmap, j: number, i: number, palette: Palette, pack: any): void {
    const side = this.blocks[j][i] & 15;
    const colorIndex = this.blocks[j][i] >> 4;
    
    const to: number[] = [
      this.blocks[j]?.[i - 1] || 0,
      this.blocks[j - 1]?.[i] || 0,
      this.blocks[j]?.[i + 1] || 0,
      this.blocks[j + 1]?.[i] || 0,
    ];
    
    const blockColor = new Color(colorIndex, palette);
    const blockSize = 18; // Original uses 18x18 pixel blocks
    
    pack.drawBlock(
      bitmap,
      (i - 4) * blockSize,
      (j - 12) * blockSize,
      side,
      blockSize,
      blockColor,
      to
    );
  }

  /**
   * Render a falling piece (current or shadow)
   * Reference: quadra/source/canvas.cc:960-996
   */
  blitBloc(bloc: Block | undefined, bitmap: Bitmap, palette: Palette): void {
    if (!bloc) return;
    
    const pack = getBlockPackForCanvas(this);
    const blockSize = 18;
    const bricks = bloc.getBlueprint();
    
    for (const brick of bricks) {
      // Calculate screen position
      let tx: number, ty: number;
      
      if (this.smooth) {
        // Smooth movement: use posX/posY (sub-pixel position)
        tx = (bloc.posX >> 4) + 4 * blockSize;
        ty = (bloc.posY >> 4) + 12 * blockSize;
      } else {
        // Block movement: use x/y (grid position)
        tx = bloc.x * blockSize;
        ty = bloc.y * blockSize;
      }
      
      tx += brick.x * blockSize;
      ty += brick.y * blockSize;
      
      const blockColor = new Color(bloc.color, palette);
      
      pack.drawBlock(
        bitmap,
        tx - 4 * blockSize,
        ty - 12 * blockSize,
        brick.t,
        blockSize,
        blockColor,
        brick.to
      );
      
      // Mark dirty regions for redraw
      const bx = Math.floor(tx / blockSize);
      const by = Math.floor(ty / blockSize);
      if (by >= 0 && by < 36 && bx >= 0 && bx < 14) {
        this.dirted[by][bx] = 2;
      }
    }
  }

  /**
   * Render line clearing flash animation
   * Reference: quadra/source/canvas.cc:862-872
   */
  blitFlash(bitmap: Bitmap, palette: Palette): void {
    const pack = getBlockPackForCanvas(this);
    const blockSize = 18;
    
    for (let j = 12; j < 32; j++) {
      for (let i = 4; i < 14; i++) {
        if (this.bflash[j][i] && !(this.bflash[j][i] & 4)) {
          // Flash color animation (cycles through colors)
          const flashColor = new Color(this.flashAnim % 8, palette);
          
          pack.drawBlock(
            bitmap,
            (i - 4) * blockSize,
            (j - 12) * blockSize,
            15, // All sides
            blockSize,
            flashColor,
            [0, 0, 0, 0]
          );
        }
      }
    }
  }

  /**
   * Render level up animation
   * Reference: quadra/source/canvas.cc:850-860
   */
  blitLevelUp(bitmap: Bitmap): void {
    // Level up sprite rendering - simplified for now
    // Original uses sprite animation, we'll add visual indicator
    if (this.level > 1) {
      // TODO: Add level up sprite/animation
    }
  }

  /**
   * Small view rendering methods (for multi-player small boards)
   * Reference: quadra/source/canvas.cc:1005-1070
   */

  smallBlitBack(bitmap: Bitmap, palette: Palette): void {
    const pack = getBlockPackForCanvas(this);
    
    for (let j = 12; j < 32; j++) {
      for (let i = 4; i < 14; i++) {
        if (this.occupied[j][i] && !this.blinded[j][i]) {
          this.drawSmallBlock(bitmap, j, i, palette, pack);
        }
      }
    }
  }

  private drawSmallBlock(bitmap: Bitmap, j: number, i: number, palette: Palette, pack: any): void {
    const side = this.blocks[j][i] & 15;
    const colorIndex = this.blocks[j][i] >> 4;
    const blockColor = new Color(colorIndex, palette);
    const blockSize = 6;
    
    pack.drawBlock(
      bitmap,
      (i - 4) * blockSize,
      (j - 12) * blockSize,
      side,
      blockSize,
      blockColor,
      [0, 0, 0, 0]
    );
  }

  smallBlitBloc(bloc: Block | undefined, bitmap: Bitmap, palette: Palette): void {
    if (!bloc) return;
    
    const pack = getBlockPackForCanvas(this);
    const blockSize = 6;
    const bricks = bloc.getBlueprint();
    
    for (const brick of bricks) {
      const tx = bloc.x * blockSize + brick.x * blockSize - 4 * blockSize;
      const ty = bloc.y * blockSize + brick.y * blockSize - 12 * blockSize;
      
      const blockColor = new Color(bloc.color, palette);
      
      pack.drawBlock(
        bitmap,
        tx,
        ty,
        brick.t,
        blockSize,
        blockColor,
        brick.to
      );
    }
  }

  smallBlitFlash(bitmap: Bitmap, palette: Palette): void {
    const pack = getBlockPackForCanvas(this);
    const blockSize = 6;
    
    for (let j = 12; j < 32; j++) {
      for (let i = 4; i < 14; i++) {
        if (this.bflash[j][i] && !(this.bflash[j][i] & 4)) {
          const flashColor = new Color(this.flashAnim % 8, palette);
          
          pack.drawBlock(
            bitmap,
            (i - 4) * blockSize,
            (j - 12) * blockSize,
            15,
            blockSize,
            flashColor,
            [0, 0, 0, 0]
          );
        }
      }
    }
  }

  /**
   * Start accumulating moves for this frame
   * Reference: quadra/source/canvas.cc:1089-1096
   */
  startMoves(): void {
    // Match original Canvas::start_moves() (canvas.cc:1089-1096)
    // Original: if(moves) delete moves; moves=new Packet_clientmoves(); moves->player = num_player;
    const wantsMoves = this.game?.getWantsMoves?.();
    if (wantsMoves) {
      if (this.movesPacket) {
        // Clean up existing packet (matches original: if(moves) delete moves;)
        this.movesPacket = null;
      }
      // Create new packet (matches original: moves=new Packet_clientmoves();)
      this.movesPacket = {
        player: this.numPlayer,
        size: 0,
        moves: []
      };
      // Note: currentMoveByte is reset in startByte() via moves->start_byte() in original
    }
  }

  /**
   * Send accumulated moves packet
   * Reference: quadra/source/canvas.cc:1098-1106
   * Sends immediately, matching original send_p_moves() behavior
   * The size check (>= 50) happens in startByte() before calling this
   */
  sendPMoves(): void {
    // Match original Canvas::send_p_moves() (canvas.cc:1098-1106)
    // Sends immediately via net->sendtcp(moves)
    // The size check (>= 50) happens in startByte() before calling this
    const wantsMoves = this.game?.getWantsMoves?.();
    if (wantsMoves && this.movesPacket && this.movesPacket.moves.length > 0) {
      if (this.movesPacket.size < 25) {
        while (this.movesPacket.size < 25) {
          this.movesPacket.moves.push(0);
          this.movesPacket.size++;
        }
      }
      // Write currentMoveByte if it has bits set (shouldn't happen, but handle it)
      if (this.currentMoveByte !== 0) {
        this.movesPacket.moves.push(this.currentMoveByte);
        this.movesPacket.size++;
        this.currentMoveByte = 0;
        this.currentMoveBitCount = 0;
      }
      
      // Send via multiplayer adapter (matches original net->sendtcp(moves))
      const adapter = this.game?.getMultiplayerAdapter?.();
      if (adapter?.sendMoves) {
        adapter.sendMoves(this.movesPacket.moves, this.movesPacket.player);
      }
      
      // Clear movesPacket after sending (matches original: moves=NULL after send_p_moves)
      this.movesPacket = null;
    }
  }

  /**
   * Start a new move byte for this frame
   * Reference: quadra/source/canvas.cc:1108-1118
   */
  startByte(): void {
    // Match original Canvas::start_byte() (canvas.cc:1108-1117)
    // Checks size >= 50 before starting new byte, sends if needed
    const wantsMoves = this.game?.getWantsMoves?.();
    if (wantsMoves) {
      if (!this.movesPacket) {
        this.startMoves();
      }
      // If packet has 50+ moves, send and start new one (matches original canvas.cc:1112)
      if (this.movesPacket && this.movesPacket.size >= 50) {
        this.sendPMoves();
        this.startMoves();
      }
      this.currentMoveByte = 0;
      this.currentMoveBitCount = 0;
    }
  }

  /**
   * Set a bit in the current move byte
   * Reference: quadra/source/canvas.cc:1120-1123
   */
  setBit(bitValue: number): void {
    // Match original Canvas::set_bit() (canvas.cc:1120-1123)
    if (this.movesPacket) {
      this.currentMoveByte |= bitValue;
    }
  }

  /**
   * Write the current move byte to the packet
   * Reference: quadra/source/canvas.cc:1125-1130
   */
  writeByte(): void {
    // Match original Canvas::write_byte() (canvas.cc:1125-1127)
    // Only increments size, doesn't check for sending
    // The size check (>= 50) happens in startByte() before starting a new byte
    if (this.movesPacket) {
      this.movesPacket.moves.push(this.currentMoveByte);
      this.movesPacket.size++;
      this.currentMoveByte = 0;
      this.currentMoveBitCount = 0;
    }
  }
}
